/* eslint-disable no-bitwise */
import { FormTemplate, FormTemplateSection } from 'data/template/template.types';
import { TargetDeliveryTemplateAccessFunction, TargetDeliveryTemplateSection } from 'data/targetDeliveryTemplateAccessFunction/targetDeliveryTemplateAccessFunction.types';
import { KeyValuePair } from 'data/types';
import { TargetModelAfCCCSettings, UtimacoAFTargetInforFormData } from 'data/target/target.types';
import {
  TargetInfoUtimaco, ICDUtimaco, NetworkTypeUtimaco, DataTypeUtimaco, MCFlags, GeneralUtimaco, MonitoringCenterTypeUtimaco,
} from './UtimacoLIMSTargetCCC';

interface AnyField extends TargetInfoUtimaco, ICDUtimaco, NetworkTypeUtimaco, DataTypeUtimaco, MCFlags {}

function getMcFlagsValue({
  connectedLinePresentation,
  separateCallContentChannel,
  suppressInterceptNumber,
} : MCFlags) : string {
  if (!connectedLinePresentation
    &&
    !separateCallContentChannel
    &&
    !suppressInterceptNumber) {
    return 'NONE';
  }

  const connectedHex = 0x2000;
  const separateHex = 0x0080;
  const suppressHex = 0x0100;

  if (connectedLinePresentation
    &&
    !separateCallContentChannel
    &&
    !suppressInterceptNumber) {
    return `0x${connectedHex.toString(16)}`;
  }

  if (connectedLinePresentation
    &&
    separateCallContentChannel
    &&
    !suppressInterceptNumber) {
    return `0x${(connectedHex | separateHex).toString(16)}`;
  }

  if (connectedLinePresentation
    &&
    !separateCallContentChannel
    &&
    suppressInterceptNumber) {
    return `0x${(connectedHex | suppressHex).toString(16)}`;
  }

  if (!connectedLinePresentation
    &&
    separateCallContentChannel
    &&
    !suppressInterceptNumber) {
    return `0x00${separateHex.toString(16)}`;
  }

  if (!connectedLinePresentation
    &&
    separateCallContentChannel
    &&
    suppressInterceptNumber) {
    return `0x0${(separateHex | suppressHex).toString(16)}`;
  }

  if (!connectedLinePresentation
    &&
    !separateCallContentChannel
    &&
    suppressInterceptNumber) {
    return `0x0${suppressHex.toString(16)}`;
  }

  // All three are checked
  return `0x${(connectedHex | separateHex | suppressHex).toString(16)}`;
}

function formatValue(key: string, fields : AnyField) : string | boolean {
  const netTypeValues = ['IA', 'IMS', 'POC', 'NGN', 'VOIP', 'GSM', 'GPRS', 'HLR', 'LTE', 'FIX'];
  const dataTypeValues = ['DATA', 'IA', 'MM', 'IRI', 'IRIMM', 'IRIPO', 'Voice', 'Voicemail'];
  const possibleValues = key === 'NETTYPE' ? netTypeValues : dataTypeValues;
  const checkedValues = Object.entries(fields).filter((field) => {
    return field[1] && possibleValues.includes(field[0]);
  }).map((value) => value[0]);

  if (checkedValues.length > 0) {
    return checkedValues.join('_');
  }

  return '';
}

// eslint-disable-next-line import/prefer-default-export
export function prepareOptions(
  targetInfoFields : TargetInfoUtimaco,
  monitoringCenterFlagsFields : MCFlags,
  ICDFields : ICDUtimaco,
  networkTypeFields : NetworkTypeUtimaco,
  callContentTypeFields : DataTypeUtimaco
) : KeyValuePair[] {

  const nettypeValue = formatValue('NETTYPE', networkTypeFields);
  const dataTypeValue = formatValue('DATATYPE', callContentTypeFields);

  const options = [
    { key: 'LEA', value: ICDFields.LEA as string },
    { key: 'LIID', value: targetInfoFields.LIID as string },
    { key: 'DIR', value: targetInfoFields.DIR as string },
    { key: 'TON', value: targetInfoFields.TON as string },
    { key: 'MCFLAGS', value: getMcFlagsValue(monitoringCenterFlagsFields) },
    { key: 'NETTYPE', value: nettypeValue as string },
    { key: 'DATATYPE', value: dataTypeValue as string },
  ];

  if (ICDFields.CLASS) {
    options.push({ key: 'CLASS', value: ICDFields.CLASS });
  }

  if (ICDFields.FILEREF) {
    options.push({ key: 'FILEREF', value: ICDFields.FILEREF });
  }

  return options;
}

export function isUtimacoTargetInfoValid(
  afSettings : { [key:string]: string | number | boolean | string[] },
) : boolean {
  if (Object.keys(afSettings).length > 0) {
    if (afSettings.LIID && afSettings.NETTYPE && afSettings.DATATYPE && afSettings.LEA) {
      return true;
    }
    return false;
  }
  return false;
}

export function getTargetTemplateValues(
  sectionName: string,
  targetTemplate: TargetDeliveryTemplateAccessFunction,
) : any {
  if (!targetTemplate) {
    return null;
  }

  const templateSection = (targetTemplate.templateSections as TargetDeliveryTemplateSection[])
    .filter((section) => section.name === sectionName)[0];

  if (!templateSection) {
    return null;
  }

  return templateSection.options.reduce((previous, current) => {
    let { value } = current;
    if (value === 'true' || value === 'false') {
      value = value === 'true' || false;
    }

    return ({ ...previous, [current.key]: value });
  },
  {});
}

export function getMapAsOptions(
  map: { [key: string]: string | boolean },
) {
  const options: KeyValuePair[] = [];

  Object.entries(map ?? {}).forEach(([key, value]) => {
    if (key === 'USEONELEMF' && typeof value === 'boolean') {
      value = value ? '1' : '0';
    }

    options.push({ key, value: value as string });
  });

  return options;
}

export function adaptToSubmitForUTM(
  targetAccessFunctionId: string,
  formData: Partial<UtimacoAFTargetInforFormData>,
  isValid: boolean,
  templateId: string | undefined
): TargetModelAfCCCSettings[] {
  const afSettings: TargetModelAfCCCSettings[] = [];

  const afids = [targetAccessFunctionId];
  const autowired = false;

  let options = prepareOptions(
    { ...formData.targetInformation },
    { ...formData.monitoringCenterFlags },
    { ...formData.ICD },
    { ...formData.networkType },
    { ...formData.callContentType }
  );

  if (templateId) {
    options = [...options, { key: 'targetDeliveryTemplate', value: templateId }];
  }
  if (options.length > 0) {
    afSettings.push({
      afids,
      autowired,
      isValid,
      options,
    });
  }

  return afSettings;
}
